<template>
    <modal
        v-if="show"
        class="modalSecurity"
        :form="form"
        :type="'is-primary'"
        :size="'is-small'"
        :title="titleModal"
        :submit-text="textButton"
        :success-text="textSuccess"
        :submitDisabled="submitDisabled"
        @submit="handleNextStep"
        @closed="handleClosed"
    >
        <template v-if="currentStep === STEPS.CHOOSE">
            <div class="modalSecurity__wrap">
                <div class="modalSecurity__title">Enable Two-Factor Authentication</div>

                <div class="modalSecurity__desc">
                    Add a second layer of verification to secure your account
                </div>

                <div class="modalSecurity__content">
                    <div class="securityTabs">
                        <div
                            v-for="(item, idx) in twoFAuthEnable"
                            :key="idx"
                            class="securityTabs__item"
                            :class="{
                                'securityTabs__item--active': item.type === activeType,
                            }"
                            @click="onChangeActiveType(item)"
                        >
                            <strong>{{ titleItem(item.type) }}</strong>
                            <span>{{ descriptionItem(item.type) }}</span>
                        </div>
                    </div>
                </div>

                <div class="modalSecurity__info">
                    Can’t access your account?
                    <a href="https://help.salesmessage.com/en/" target="_blank">
                        Contact Support
                    </a>
                </div>
            </div>
        </template>

        <template v-if="currentStep === STEPS.NUMBER">
            <div class="modalSecurity__wrap">
                <div class="modalSecurity__title">Add your mobile phone number</div>

                <div class="modalSecurity__desc" v-html="description" />

                <div class="modalSecurity__content">
                    <form-input
                        v-model="number"
                        :placeholder="'(555) 123-5432'"
                        :formatter="'phone'"
                    />
                </div>
            </div>
        </template>

        <template v-if="currentStep === STEPS.CODE">
            <div class="modalSecurity__wrap">
                <div class="modalSecurity__title" v-html="title" />
                <div class="modalSecurity__desc" v-html="description" />

                <template v-if="isApp && !isLogin">
                    <div v-if="!imageQrCode" class="modalSecurity__imageQR">
                        <basic-loader center aria-label="ModalSecurity" />
                    </div>
                    <div v-if="imageQrCode" class="modalSecurity__imageQR" v-html="imageQrCode" />

                    <div class="modalSecurity__info">
                        Enter the 6-digit verification code from the authenticator app
                    </div>

                    <div class="modalSecurity__content">
                        <code-press :code="code" :error="codeError" @update="handleWatchCode" />
                    </div>
                    <div class="modalSecurity__info mb-0">
                        Need help?
                        <a href="https://help.salesmessage.com/en/" target="_blank">
                            Contact Support</a
                        >
                    </div>
                </template>
                <template v-else>
                    <div class="modalSecurity__content">
                        <code-press :code="code" :error="codeError" @update="handleWatchCode" />
                    </div>
                    <div v-if="!isApp" class="modalSecurity__info">
                        Haven't received it?
                        <a :class="{ 'is-disabled': disabledSend }" @click.prevent="handleSendCode">
                            <span v-if="disabledSend">Sending</span>
                            <span v-else>Resend a new code</span>
                        </a>
                    </div>
                    <div class="modalSecurity__info">
                        Need help?
                        <a href="https://help.salesmessage.com/en/" target="_blank">
                            Contact Support
                        </a>
                    </div>
                    <div
                        v-if="isLogin && twoFAuthEnable.length > 1"
                        class="modalSecurity__info modalSecurity__info--different"
                    >
                        Use a different
                        <a href="" @click.prevent="onDifferentMethod"> method </a>
                    </div>
                </template>
            </div>
        </template>
    </modal>
</template>

<script>
import { mapState } from 'pinia';
import stores from 'store/modules';
import { debounce } from 'lodash';
import validator from 'utils/validator';
import Form from 'core/Form';
import CodePress from './CodePress';

const STEPS = {
    CHOOSE: 1,
    NUMBER: 2,
    CODE: 3,
};

export default {
    components: {
        CodePress,
    },

    data() {
        return {
            form: new Form({}),
            number: '',
            code: '',
            codeError: '',
            currentStepIndex: 0,
            activeType: '',
            disabledSend: false,
            disabledSendTimeout: null,
            imageQrCode: null,
            STEPS: STEPS,
        };
    },

    computed: {
        show() {
            return this.twoFAuthShowModal;
        },

        item() {
            return this.twoFAuthShowModalItem;
        },

        type() {
            return this.twoFAuthShowModalType;
        },

        submitDisabled() {
            if (this.isLogin) {
                if (this.isSms) {
                    if (this.currentStep === STEPS.CHOOSE) return !this.activeType;
                    if (this.currentStep === STEPS.NUMBER)
                        return !validator.isMobilePhone(this.number);
                    if (this.currentStep === STEPS.CODE) return this.form.busy;
                }

                if (this.isEmail) {
                    if (this.currentStep === STEPS.CHOOSE) return !this.activeType;
                    if (this.currentStep === STEPS.CODE) return this.form.busy;
                }
            }

            if (this.isSettings) {
                if (this.isSms) {
                    if (this.currentStep === STEPS.NUMBER)
                        return !validator.isMobilePhone(this.number);
                    if (this.currentStep === STEPS.CODE) return this.form.busy;
                }

                if (this.isEmail) {
                    if (this.currentStep === STEPS.CODE) return this.form.busy;
                }
            }

            return this.form.busy;
        },

        titleModal() {
            if (this.form.successful) return 'Verify Your Account';

            if (this.isLogin) {
                if (this.isSms || this.isEmail || this.isApp) {
                    if (this.currentStep === STEPS.CHOOSE) return 'Verification Method';
                    if (this.currentStep === STEPS.CODE) return 'Verification Required';
                }

                return 'Verification Method';
            }

            if (this.isSettings) {
                if (this.isEnabled) return `Disable ${this.typeLabel}`;

                if (this.isSms) {
                    if (this.currentStep === STEPS.NUMBER) return 'Phone Number';
                }

                if (this.currentStep === STEPS.CODE) return 'Verify Your Account';
            }

            return '';
        },

        textButton() {
            if (this.isLogin) {
                if (this.currentStep === STEPS.CHOOSE) return 'Continue';

                return 'Confirm';
            }

            return 'Confirm';
        },

        textSuccess() {
            return 'Your account was successfully verified';
        },

        title() {
            if (this.isSms || this.isEmail) {
                return 'One-Time Passcode';
            }

            if (this.isApp) {
                if (this.isLogin) {
                    return 'One-Time Passcode';
                }

                return 'Scan QR Code';
            }

            return '';
        },

        description() {
            if (this.isSms) {
                if (this.currentStep === STEPS.NUMBER)
                    return 'Enter your mobile phone number to receive a verification code.';

                return `An SMS was sent to ${this.item.label}. Please enter the<br /> verification code.`;
            }

            if (this.isEmail) {
                return `An email with a temporary 6-digit code was just sent to you.<br /><span>${this.item.label}</span>`;
            }

            if (this.isApp) {
                if (this.isLogin) {
                    return 'Enter code from app that you choosen';
                }

                return 'Scan the QR code below using an <a href="http://help.salesmessage.com/en/articles/6797119-two-factor-authorization" target="_blank">authenticator app</a>.';
            }

            return '';
        },

        isSettings() {
            return this.type === 'settings';
        },

        isLogin() {
            return this.type === 'login';
        },

        isSms() {
            return this.item.type === 'sms';
        },

        isEmail() {
            return this.item.type === 'email';
        },

        isApp() {
            return this.item.type === 'app';
        },

        isEnabled() {
            return this.item.value;
        },

        stepsAccess() {
            if (this.isLogin) {
                if (this.isSms || this.isEmail || this.isApp) return [STEPS.CHOOSE, 3];
            }

            if (this.isSettings) {
                if (this.isEmail) return [STEPS.CODE];
                if (this.isSms) return [STEPS.NUMBER, STEPS.CODE];
                if (this.isApp) return [STEPS.CODE];

                return [];
            }

            return [];
        },

        currentStep() {
            return this.stepsAccess[this.currentStepIndex];
        },

        typeLabel() {
            if (this.isSms) return 'SMS';
            if (this.isEmail) return 'Email';
            if (this.isApp) return 'Authenticator App';

            return '';
        },

        ...mapState(stores.User, ['user']),
        ...mapState(stores.Auth2f, [
            'twoFAuth',
            'twoFAuthEnable',
            'twoFAuthShowModal',
            'twoFAuthShowModalItem',
            'twoFAuthShowModalType',
        ]),
    },

    watch: {
        async show(status) {
            if (status) {
                this.init();
            } else {
                this.reset();
            }
        },
    },

    methods: {
        async init() {
            this.debounceHandleSendVerify = debounce(this.handleSendVerify, 1000);

            await this.initStep();
        },

        async reset() {
            this.form.reset();
            this.number = '';
            this.code = '';
            this.codeError = '';
            this.currentStepIndex = 0;
            this.activeType = '';
            this.disabledSend = false;
            this.imageQrCode = null;
            clearTimeout(this.disabledSendTimeout);
        },

        async initStep() {
            if (this.isLogin) {
                const stepCode = await this.initStepForCode();

                this.activeType = this.twoFAuthShowModalItem.type;

                if (this.isSms || this.isEmail) {
                    if (stepCode) {
                        this.setStep(STEPS.CODE);
                        await this.handleSendCode(false);

                        return;
                    }

                    this.setStep(STEPS.CHOOSE);
                }

                if (this.isApp) {
                    if (stepCode) {
                        this.setStep(STEPS.CODE);

                        return;
                    }

                    this.setStep(STEPS.CHOOSE);
                }
            }

            if (this.isSettings) {
                if (this.isEmail) {
                    this.setStep(STEPS.CODE);
                    await this.handleSendCode(false);

                    return;
                }

                if (this.isSms) {
                    if (!this.user.number) {
                        this.setStep(STEPS.NUMBER);

                        return;
                    }

                    this.setStep(STEPS.CODE);
                    await this.handleSendCode(false);

                    return;
                }

                if (this.isApp) {
                    this.setStep(STEPS.CODE);
                    await this.handleQrCode();

                    return;
                }
            }
        },

        initStepForCode() {
            let remember = this.$cookie.get('remember');
            remember = typeof remember === 'string' ? JSON.parse(remember) : {};
            const item = this.twoFAuthEnable.find((item) => item.type === remember.type);

            if (this.twoFAuthEnable.length === 1) {
                this.$store.dispatch('updateTwoFAuthState', {
                    twoFAuthShowModalItem: this.twoFAuthEnable[0],
                });

                return true;
            }

            if (item) {
                this.$store.dispatch('updateTwoFAuthState', { twoFAuthShowModalItem: item });

                return true;
            }

            return false;
        },

        setStep(step) {
            this.currentStepIndex = this.getStep(step);
        },

        getStep(step) {
            return this.stepsAccess.findIndex((i) => i === step);
        },

        async handleSendCode(status = true) {
            try {
                this.form.busy = true;

                if (this.disabledSend) return;

                if (status) {
                    this.disabledSend = true;

                    this.disabledSendTimeout = setTimeout(() => {
                        this.disabledSend = false;
                    }, 15000);
                }

                if (this.isLogin) {
                    const response = await this.$store.dispatch('createTwoFAuthAuthSend', {
                        params: {
                            type: this.item.type,
                        },
                    });

                    if (response.status === 401) {
                        await this.$store.dispatch('showToast', {
                            type: 'danger',
                            message: 'Your session expired, please log in.',
                        });

                        setTimeout(() => {
                            this.handleClosed();
                        }, 1500);
                    }
                }

                if (this.isSettings) {
                    await this.$store.dispatch('createTwoFAuthSend', {
                        params: {
                            type: this.item.type,
                        },
                    });
                }
            } catch (e) {
                this.$log.error(e, this.$options.__file, 'handleSendCode');
            } finally {
                this.form.busy = false;
            }
        },

        async handleSendVerify() {
            try {
                this.form.busy = true;

                if (this.isLogin) {
                    const response = await this.$store.dispatch('updateTwoFAuthAuthVerify', {
                        params: {
                            type: this.item.type,
                            value: true,
                            code: this.code,
                        },
                    });

                    const token = response?.token?.access_token;

                    if (token) {
                        localStorage.setItem('token', token);
                    }

                    if (response.status === 422) {
                        this.codeError = 'Wrong code, please try again';
                        this.form.busy = false;

                        return;
                    }

                    if (response.status === 401) {
                        await this.$store.dispatch('showToast', {
                            type: 'danger',
                            message: 'Your session expired, please log in.',
                        });

                        setTimeout(() => {
                            this.handleClosed();
                        }, 1500);

                        return;
                    }

                    if (response?.data?.status === 429) {
                        this.$store.dispatch('showToast', {
                            type: 'danger',
                            message: 'Too many attempts. Try again later.',
                        });

                        this.form.busy = false;

                        return;
                    }

                    await this.$store.dispatch('getUserMe', { update: false });

                    this.form.busy = true;

                    this.handleRememberDevice();

                    this.form.successful = true;

                    setTimeout(() => {
                        this.handleClosed();

                        window.location = this.$route.query.redirect || '/conversations';
                    }, 1500);
                }

                if (this.isSettings) {
                    await this.$store.dispatch('updateTwoFAuthVerify', {
                        params: {
                            type: this.item.type,
                            value: !this.item.value,
                            code: this.code,
                        },
                    });

                    this.form.successful = true;

                    setTimeout(() => {
                        this.handleClosed();
                    }, 1500);
                }
            } catch (e) {
                this.$log.error(e, this.$options.__file, 'handleSendVerify');

                this.codeError = 'Wrong code, please try again';
            } finally {
                this.form.busy = false;
            }
        },

        async handleQrCode() {
            try {
                const { qr_code } = await this.$store.dispatch('getTwoFAuthQrCode');

                this.imageQrCode = qr_code;
            } catch (e) {
                this.$log.error(e, this.$options.__file, 'handleQrCode');
            }
        },

        async handleWatchCode({ ready, code }) {
            this.codeError = '';
            this.code = code;

            if (ready) {
                await this.debounceHandleSendVerify();
            }
        },

        async handleUpdateNumber() {
            try {
                this.form.busy = true;

                if (this.isSettings) {
                    await this.$store.dispatch('updateUsersProfileNumber', {
                        params: {
                            number: this.number,
                        },
                    });

                    const items = await this.$store.dispatch('getTwoFAuthSettings', {
                        loading: false,
                    });

                    await this.$store.dispatch('updateTwoFAuthState', {
                        twoFAuthShowModalItem: items.find((item) => item.type === this.item.type),
                    });

                    this.setStep(3);
                }
            } catch (e) {
                this.$log.error(e, this.$options.__file, 'handleUpdateNumber');
            } finally {
                this.form.busy = false;
            }
        },

        async handleNextStep() {
            if (this.isLogin) {
                if (this.isSms || this.isEmail) {
                    if (this.currentStep === STEPS.CHOOSE) {
                        this.setStep(STEPS.CODE);
                        await this.handleSendCode(false);
                        return;
                    }

                    if (this.currentStep === STEPS.CODE) {
                        await this.handleSendVerify();

                        return;
                    }
                }

                if (this.isApp) {
                    if (this.currentStep === STEPS.CHOOSE) {
                        this.setStep(STEPS.CODE);
                        return;
                    }

                    if (this.currentStep === STEPS.CODE) {
                        await this.handleSendVerify();

                        return;
                    }
                }
            }

            if (this.isSettings) {
                if (this.isEmail) {
                    if (this.currentStep === STEPS.CODE) {
                        await this.handleSendVerify();

                        return;
                    }
                }

                if (this.isSms) {
                    if (this.currentStep === STEPS.NUMBER) {
                        await this.handleUpdateNumber();
                        await this.handleSendCode(false);

                        return;
                    }

                    if (this.currentStep === STEPS.CODE) {
                        await this.handleSendVerify();

                        return;
                    }
                }

                if (this.isApp) {
                    await this.handleSendVerify();

                    return;
                }
            }
        },

        handleClosed() {
            this.$store.dispatch('handleShowModalTwoFAuth', { status: false });
        },

        handleRememberDevice() {
            let data = this.$cookie.get('remember');

            if (data) {
                data = JSON.parse(data);

                if (data.type) return;

                data.type = this.item.type;

                this.$cookie.set('remember', JSON.stringify(data), '14D');
            }
        },

        onChangeActiveType(item) {
            this.activeType = item.type;
            this.$store.dispatch('updateTwoFAuthState', { twoFAuthShowModalItem: item });
        },

        onDifferentMethod() {
            let data = this.$cookie.get('remember');

            if (data) {
                data = JSON.parse(data);
                data.type = null;

                this.$cookie.set('remember', JSON.stringify(data), '14D');
            }

            this.code = '';
            this.codeError = '';
            this.setStep(1);
        },

        titleItem(type) {
            if (type === 'sms') return 'SMS';
            if (type === 'email') return 'Email';
            if (type === 'app') return 'Authenticator App';

            return '';
        },

        descriptionItem(type) {
            if (type === 'sms') return 'Receive a one-time passcode via SMS';
            if (type === 'email') return 'Receive a one-time passcode via email';
            if (type === 'app') return 'Receive a one-time passcode via an authenticator app';

            return '';
        },
    },
};
</script>
